import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { DefaultSecondaryButton } from '@frontend/components/common';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { DateBadge } from '@frontend/elements';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { NotificationContentClient } from '@frontend/notification/api';
import { NotificationContent } from '@frontend/notification/types';
import { FormattedMessage } from 'react-intl';

import { ApplicationCreateButton } from '../../../components/create-button';
import OverviewHeader from '../../../header/overview-header';
import { CreateContentModal } from '../content-detail/create-content-modal/create-content-modal.component';
import { UpdateTemplateModal } from '../update-template-modal/update-template-modal.component';
import { useTemplateDetail } from './template-detail.controller';

export const TemplateDetail = () => {
    const viewProps = useTemplateDetail();

    if (!viewProps.template) {
        return (
            <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-center justify-center'>
                <Spinner className='text-border-default fill-brand-primary size-8' />
            </div>
        );
    }

    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                showBack
                title={CommonMessage.OBJECTS.NOTIFICATION_TEMPLATE.DEFAULT}
                createButton={
                    <ApplicationCreateButton
                        modalId={viewProps.contentModalId}
                        renderDefaultModal={() => (
                            <CreateContentModal
                                templateId={viewProps.template!.id}
                                onClose={() => viewProps.closeModal(viewProps.contentModalId)}
                            />
                        )}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[900px]' }}
                        createPermissions={viewProps.createContentPermissions}
                        entity={<span className='lowercase'>{CommonMessage.OBJECTS.NOTIFICATION_CONTENT.DEFAULT}</span>}
                    />
                }
            />
            <div className='inline-flex items-start justify-start gap-4 self-stretch'>
                <div className='bg-background-surface outline-border-default inline-flex w-96 flex-col items-start justify-start gap-6 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                    <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                    <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.NOTIFICATION_TEMPLATE.DEFAULT}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.template.name}</div>
                        </div>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.DESCRIPTION}</div>
                            <div className='text-foreground-default justify-start self-stretch text-base font-medium'>{viewProps.template.description}</div>
                        </div>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                <DateBadge
                                    date={viewProps.template.creation_timestamp}
                                    inline
                                    displayTime
                                />
                            </div>
                        </div>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.UPDATED_AT}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                <DateBadge
                                    date={viewProps.template.update_timestamp}
                                    inline
                                    displayTime
                                />
                            </div>
                        </div>
                    </div>
                    <AuthorizationWrapper
                        requiredPermissions={viewProps.updateTemplatePermissions}
                        showLoading={false}>
                        <DefaultSecondaryButton
                            text={
                                <FormattedMessage
                                    id='Applications.NOTIFICATIONS.TEMPLATE_DETAIL.UPDATE_TEMPLATE'
                                    description='Label for update template button'
                                    defaultMessage='Update template'
                                />
                            }
                            onClick={() =>
                                viewProps.openModal(
                                    viewProps.templateModalId,
                                    () => (
                                        <UpdateTemplateModal
                                            template={viewProps.template!}
                                            callBack={viewProps.setTemplate}
                                        />
                                    ),
                                    {},
                                    { type: ModalType.PANE, className: 'max-w-[900px]' }
                                )
                            }
                        />
                    </AuthorizationWrapper>
                </div>
                <ReactiveObjectList<NotificationContent>
                    fetch={(arg) => NotificationContentClient.fetchNotificationContents({ template_id: viewProps.template!.id, ...arg })}
                    type={EntityType.NOTIFICATION_CONTENT}
                    dispatch={viewProps.dispatch}
                    columns={{
                        columns: ['channel', 'language', 'subject'],
                        delete: {
                            deleteAction: (accountId: string, templateId: string, contentId: string) =>
                                NotificationContentClient.deleteNotificationContent(accountId, templateId, contentId),
                            deleteKeys: ['account_id', 'template_id', 'id'],
                            displayName: (value) => `${value.subject} - ${value.channel} - ${value.language}`,
                            requiredPermissions: viewProps.deleteContentPermissions
                        }
                    }}
                    onSelectElement={(obj) => viewProps.navigate(`../content/${obj.id}/details`)}
                    eventListener={EntityType.NOTIFICATION_CONTENT}
                />
            </div>
        </div>
    );
};
